// src/components/admin/AdminPostForm.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Category, BlogPost, BlogPostFormData, PostSection } from '../../types/blog';
import api from "../../api/axiosInstance";

const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080'
    : 'https://api.sonabelanska.sk';

// ─────────────────────────────────────────────────────────────────────────────
// Styled Components (unchanged – kept exactly as you had them)
// ─────────────────────────────────────────────────────────────────────────────
const FormContainer = styled.div`
    max-width: 900px;
    margin: 2rem auto;
    background: #ffffff;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;
const FormTitle = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 2rem;
    text-align: center;
`;
const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;
const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
const Label = styled.label`
    font-weight: 600;
    color: #333333;
    font-size: 0.95rem;
`;
const Input = styled.input`
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;
    &:focus { outline: none; border-color: #990147; box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1); }
`;
const TextArea = styled.textarea`
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    min-height: 100px;
    resize: vertical;
    font-family: inherit;
    transition: all 0.3s ease;
    &:focus { outline: none; border-color: #990147; box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1); }
`;
const Select = styled.select`
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    transition: all 0.3s ease;
    &:focus { outline: none; border-color: #990147; box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1); }
`;
const SectionsContainer = styled.div`
    border: 2px dashed #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
`;
const SectionHeader = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    color: #333333;
    margin-bottom: 1rem;
`;
const SectionItem = styled(motion.div)`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
`;
const SectionControls = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
`;
const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' | 'danger' }>`
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    ${({ variant = 'primary' }) => {
        switch (variant) {
            case 'primary': return `background: linear-gradient(135deg, #990147 0%, #cc3366 100%); color: white; &:hover { box-shadow: 0 4px 15px rgba(153, 1, 71, 0.3); }`;
            case 'secondary': return `background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; &:hover { background: #e2e8f0; }`;
            case 'danger': return `background: #dc2626; color: white; &:hover { background: #b91c1c; }`;
        }
    }}
    &:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }
`;
const ErrorMessage = styled.div` color: #dc2626; font-size: 0.875rem; margin-top: 0.25rem; `;
const SuccessMessage = styled(motion.div)`
    background: #dcfce7; color: #166534; padding: 1rem; border-radius: 8px; border: 1px solid #bbf7d0; margin-bottom: 1rem;
`;
const LoadingSpinner = styled.div`
    display: inline-block; width: 1rem; height: 1rem; border: 2px solid #ffffff; border-radius: 50%; border-top-color: transparent;
    animation: spin 1s ease-in-out infinite; margin-right: 0.5rem;
    @keyframes spin { to { transform: rotate(360deg); } }
`;
const ButtonGroup = styled.div`
    display: flex; gap: 1rem; justify-content: flex-end; margin-top: 2rem;
`;
const ImagePreview = styled.div` margin-top: 0.5rem; img { max-width: 200px; max-height: 150px; border-radius: 8px; border: 2px solid #e2e8f0; } `;
const FileInputContainer = styled.div` display: flex; flex-direction: column; gap: 0.5rem; `;
const FileName = styled.span` font-size: 0.875rem; color: #64748b; font-style: italic; `;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type ClientSection = {
    sectionType: PostSection['sectionType'];
    content: string;
    sectionOrder: number;
    clientId: string; // stable key for React list
};

interface AdminPostFormProps {
    post?: BlogPost;
    onSuccess?: () => void;
    onCancel?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const AdminPostForm: React.FC<AdminPostFormProps> = ({ post, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState<{
        title: string;
        excerpt: string;
        featuredImage: File | null;
        categoryId: number;
        readTime: string;
        sections: ClientSection[];
    }>({
        title: '',
        excerpt: '',
        featuredImage: null,
        categoryId: 0,
        readTime: '',
        sections: [],
    });

    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [imagePreview, setImagePreview] = useState<string>('');

    // Load existing post
    useEffect(() => {
        if (post) {
            setFormData({
                title: post.title || '',
                excerpt: post.excerpt || '',
                featuredImage: null,
                categoryId: post.category?.id || 0,
                readTime: post.readTime || '',
                sections: (post.sections || []).map((s, i) => ({
                    sectionType: s.sectionType,
                    content: s.content,
                    sectionOrder: s.sectionOrder,
                    clientId: `existing-${s.id ?? i}-${Math.random().toString(36).slice(2, 9)}`,
                })),
            });
            if (post.featuredImage) setImagePreview(post.featuredImage);
        }
    }, [post]);

    // Fetch categories
    useEffect(() => {
        api.get(`${API_BASE_URL}/api/categories`)
            .then(r => setCategories(r.data))
            .catch(() => setError('Nepodarilo sa načítať kategórie'));
    }, []);

    const generateClientId = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    const addSection = (type: ClientSection['sectionType']) => {
        setFormData(prev => ({
            ...prev,
            sections: [...prev.sections, {
                sectionType: type,
                content: '',
                sectionOrder: prev.sections.length,
                clientId: generateClientId(),
            }],
        }));
    };

    const updateSection = (clientId: string, field: 'sectionType' | 'content', value: string) => {
        setFormData(prev => ({
            ...prev,
            sections: prev.sections.map(s =>
                s.clientId === clientId ? { ...s, [field]: value } : s
            ),
        }));
    };

    const removeSection = (clientId: string) => {
        setFormData(prev => {
            const filtered = prev.sections.filter(s => s.clientId !== clientId);
            return {
                ...prev,
                sections: filtered.map((s, i) => ({ ...s, sectionOrder: i })),
            };
        });
    };

    const moveSection = (clientId: string, direction: 'up' | 'down') => {
        setFormData(prev => {
            const idx = prev.sections.findIndex(s => s.clientId === clientId);
            if (idx === -1) return prev;
            if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === prev.sections.length - 1)) return prev;

            const newIdx = direction === 'up' ? idx - 1 : idx + 1;
            const newSections = [...prev.sections];
            [newSections[idx], newSections[newIdx]] = [newSections[newIdx], newSections[idx]];

            return {
                ...prev,
                sections: newSections.map((s, i) => ({ ...s, sectionOrder: i })),
            };
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'categoryId' ? parseInt(value) : value,
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFormData(prev => ({ ...prev, featuredImage: file }));
        if (file) {
            const reader = new FileReader();
            reader.onload = ev => setImagePreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setImagePreview('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (!formData.title.trim() || formData.categoryId === 0 || formData.sections.length === 0) {
            setError('Vyplňte názov, vyberte kategóriu a pridajte aspoň jednu sekciu');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('jwt');
            const data = new FormData();
            data.append('title', formData.title);
            data.append('excerpt', formData.excerpt || '');
            data.append('categoryId', formData.categoryId.toString());
            data.append('readTime', formData.readTime || '');
            if (formData.featuredImage) data.append('featuredImage', formData.featuredImage);

            const cleanSections = formData.sections.map(({ clientId, ...s }) => s);
            data.append('sections', JSON.stringify(cleanSections));

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            };

            if (post) {
                await api.put(`${API_BASE_URL}/api/posts/${post.id}`, data, config);
                setSuccess('Článok bol úspešne upravený!');
            } else {
                await api.post(`${API_BASE_URL}/api/posts`, data, config);
                setSuccess('Článok bol úspešne vytvorený!');
                // Reset form for new post
                setFormData({
                    title: '',
                    excerpt: '',
                    featuredImage: null,
                    categoryId: 0,
                    readTime: '',
                    sections: [],
                });
                setImagePreview('');
            }

            onSuccess?.();
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.message || 'Nepodarilo sa uložiť článok');
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormContainer>
            <FormTitle>{post ? 'Upraviť článok' : 'Vytvoriť nový článok'}</FormTitle>

            {success && <SuccessMessage initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{success}</SuccessMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Form onSubmit={handleSubmit}>
                {/* Title */}
                <FormGroup>
                    <Label htmlFor="title">Názov článku *</Label>
                    <Input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                    />
                </FormGroup>

                {/* Excerpt */}
                <FormGroup>
                    <Label htmlFor="excerpt">Krátky popis</Label>
                    <TextArea
                        id="excerpt"
                        name="excerpt"
                        value={formData.excerpt}
                        onChange={handleInputChange}
                        rows={3}
                    />
                </FormGroup>

                {/* Featured image */}
                <FormGroup>
                    <Label htmlFor="featuredImage">Hlavný obrázok</Label>
                    <FileInputContainer>
                        <Input type="file" id="featuredImage" accept="image/*" onChange={handleFileChange} />
                        {formData.featuredImage && <FileName>{formData.featuredImage.name}</FileName>}
                        {imagePreview && (
                            <ImagePreview>
                                <img src={imagePreview} alt="Náhľad" />
                            </ImagePreview>
                        )}
                    </FileInputContainer>
                </FormGroup>

                {/* Category */}
                <FormGroup>
                    <Label htmlFor="categoryId">Kategória *</Label>
                    <Select id="categoryId" name="categoryId" value={formData.categoryId} onChange={handleInputChange} required>
                        <option value={0}>Vyberte kategóriu</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                </FormGroup>

                {/* Read time */}
                <FormGroup>
                    <Label htmlFor="readTime">Čas čítania</Label>
                    <Input
                        type="text"
                        id="readTime"
                        name="readTime"
                        value={formData.readTime}
                        onChange={handleInputChange}
                        placeholder="napr. 5 min"
                    />
                </FormGroup>

                {/* Sections */}
                <SectionsContainer>
                    <SectionHeader>Sekcie článku *</SectionHeader>

                    {formData.sections.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                            Zatiaľ nie sú pridané žiadne sekcie
                        </div>
                    )}

                    {formData.sections.map(section => (
                        <SectionItem
                            key={section.clientId}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <FormGroup>
                                <Label>Typ sekcie</Label>
                                <Select
                                    value={section.sectionType}
                                    onChange={e => updateSection(section.clientId, 'sectionType', e.target.value)}
                                >
                                    <option value="HEADING">Nadpis</option>
                                    <option value="PARAGRAPH">Odsek</option>
                                    <option value="QUOTE">Citát</option>
                                </Select>
                            </FormGroup>

                            <FormGroup>
                                <Label>Obsah</Label>
                                <TextArea
                                    value={section.content}
                                    onChange={e => updateSection(section.clientId, 'content', e.target.value)}
                                    rows={section.sectionType === 'QUOTE' ? 3 : 6}
                                    placeholder={
                                        section.sectionType === 'HEADING' ? 'Nadpis sekcie...'
                                            : section.sectionType === 'QUOTE' ? 'Text citátu...'
                                                : 'Text odseku...'
                                    }
                                />
                            </FormGroup>

                            <SectionControls>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => moveSection(section.clientId, 'up')}
                                    disabled={formData.sections.findIndex(s => s.clientId === section.clientId) === 0}
                                >
                                    ↑ Hore
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => moveSection(section.clientId, 'down')}
                                    disabled={formData.sections.findIndex(s => s.clientId === section.clientId) === formData.sections.length - 1}
                                >
                                    ↓ Dole
                                </Button>
                                <Button type="button" variant="danger" onClick={() => removeSection(section.clientId)}>
                                    Odstrániť
                                </Button>
                            </SectionControls>
                        </SectionItem>
                    ))}

                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Button type="button" variant="secondary" onClick={() => addSection('HEADING')}>+ Nadpis</Button>
                        <Button type="button" variant="secondary" onClick={() => addSection('PARAGRAPH')}>+ Odsek</Button>
                        <Button type="button" variant="secondary" onClick={() => addSection('QUOTE')}>+ Citát</Button>
                    </div>
                </SectionsContainer>

                {/* Buttons */}
                <ButtonGroup>
                    {onCancel && (
                        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
                            Zrušiť
                        </Button>
                    )}
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                    >
                        {loading && <LoadingSpinner />}
                        {loading ? 'Ukladá sa...' : post ? 'Upraviť článok' : 'Vytvoriť článok'}
                    </Button>
                </ButtonGroup>
            </Form>
        </FormContainer>
    );
};

export default AdminPostForm;